import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axiosInstance";
import { useDispatch } from "react-redux";
import { setWishlistItems } from "../../redux/wishlistSlice";
import "./login.css";

const getProductImage = (product) =>
  product.images?.[0]?.image?.image || product.product?.image?.image || "";

const normalizeWishlistItem = (item) => {
  const variant = item.varient || {};
  return {
    id: variant.id,
    wishlistId: item.id,
    name: variant.name,
    slug: item.slug || variant.slug,
    image: getProductImage(variant),
    type: item.type === "rental" || item.type === "rent" ? "rent" : "buy",
    sale_price: variant.prices?.sale_price,
    sale_offer_price: variant.prices?.sale_offer_price,
    rental_price: variant.prices?.rental_price,
    offerPrice:
      item.type === "rental" || item.type === "rent"
        ? variant.prices?.rental_price
        : variant.prices?.sale_offer_price || variant.prices?.sale_price,
  };
};

function LoginModal({ show, onHide, onLoginSuccess }) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const otpRefs = useRef([...Array(4)].map(() => React.createRef()));
  const [step, setStep] = useState("contact");
  const [contact, setContact] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loginData, setLoginData] = useState(null);

  useEffect(() => {
    if (show) setTimeout(() => inputRef.current?.focus(), 120);
  }, [show]);

  const reset = () => {
    setStep("contact");
    setContact("");
    setOtpDigits(["", "", "", ""]);
    setMessage("");
    setError("");
    setLoading(false);
    setName("");
    setEmail("");
    setLoginData(null);
  };

  const close = () => { reset(); onHide?.(); };

  const sendOtp = async () => {
    const nextContact = contact.trim();
    if (/^\d/.test(nextContact)) {
      if (!/^\d{10}$/.test(nextContact)) {
        setError("Enter a valid 10-digit phone number.");
        return;
      }
    } else {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextContact)) {
        setError("Enter a valid email address.");
        return;
      }
    }
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const formattedContact = /^\d{10}$/.test(nextContact)
        ? `+1 (${nextContact.slice(0,3)}) ${nextContact.slice(3,6)}-${nextContact.slice(6)}`
        : nextContact;

      const { data } = await axiosInstance.post("accounts/validate_contact/", {
        contact: formattedContact,
        role: "Customer",
      });
      setMessage(data?.message || `OTP sent to ${nextContact}`);
      toast.success(data?.message || "OTP sent");
      setStep("otp");
      setOtpDigits(["", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.current?.focus(), 120);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (overrideDigits) => {
    const digits = Array.isArray(overrideDigits) ? overrideDigits : otpDigits;
    const otp = digits.join("");
    if (otp.length !== 4) { setError("Enter the 4-digit OTP."); return; }
    setLoading(true);
    setError("");
    try {
      const trimmedContact = contact.trim();
      const formattedContact = /^\d{10}$/.test(trimmedContact)
        ? `+1 (${trimmedContact.slice(0,3)}) ${trimmedContact.slice(3,6)}-${trimmedContact.slice(6)}`
        : trimmedContact;

      const { data } = await axiosInstance.post("accounts/verify_otp/", {
        contact: formattedContact,
        otp,
      });
      if (data?.token) localStorage.setItem("token", data.token);
      if (data?.user_id) localStorage.setItem("userid", data.user_id);
      if (data?.store_id) localStorage.setItem("store_id", data.store_id);

      if (!data?.name) {
        setLoginData(data);
        setStep("name");
      } else {
        localStorage.setItem("name", data.name);
        if (data.user_id) {
          try {
            const res = await axiosInstance.get("catlog/wishlists/", {
              params: { user: data.user_id },
            });
            const results = Array.isArray(res.data?.results) ? res.data.results : [];
            const normalizedItems = results.map(normalizeWishlistItem).filter((item) => item.id);
            dispatch(setWishlistItems(normalizedItems));
          } catch (err) {
            console.error("Failed to fetch wishlist", err);
          }
        }
        window.dispatchEvent(new Event("authChange"));
        onLoginSuccess?.(data);
        toast.success("Logged in successfully");
        close();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };

  const saveName = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Valid email is required");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const userId = localStorage.getItem("userid");
      if (userId) {
        await axiosInstance.patch(`user_data/users/${userId}/`, {
          full_name: name.trim(),
          email: email.trim()
        });
      }
      localStorage.setItem("name", name.trim());
      if (userId) {
        try {
          const res = await axiosInstance.get("catlog/wishlists/", {
            params: { user: userId },
          });
          const results = Array.isArray(res.data?.results) ? res.data.results : [];
          const normalizedItems = results.map(normalizeWishlistItem).filter((item) => item.id);
          dispatch(setWishlistItems(normalizedItems));
        } catch (err) {
          console.error("Failed to fetch wishlist", err);
        }
      }
      window.dispatchEvent(new Event("authChange"));
      onLoginSuccess?.({ ...loginData, name: name.trim(), email: email.trim() });
      toast.success("Logged in successfully");
      close();
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to save name."
      );
    } finally {
      setLoading(false);
    }
  };

  const updateOtpDigit = (value, index) => {
    if (loading) return;
    let nextDigits = [...otpDigits];
    if (value.length > 1) {
      const cleaned = value.replace(/\D/g, "").slice(0, 4);
      if (!cleaned) return;
      nextDigits = ["", "", "", ""];
      for (let i = 0; i < cleaned.length; i++) nextDigits[i] = cleaned[i];
      setOtpDigits(nextDigits);
      if (cleaned.length === 4) {
        otpRefs.current[3]?.current?.focus();
        verifyOtp(nextDigits);
      } else {
        otpRefs.current[cleaned.length]?.current?.focus();
      }
      return;
    }

    if (!/^\d?$/.test(value)) return;
    nextDigits[index] = value;
    setOtpDigits(nextDigits);
    if (value && index < 3) otpRefs.current[index + 1]?.current?.focus();
    if (nextDigits.join("").length === 4) {
      verifyOtp(nextDigits);
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Enter") {
      verifyOtp();
    } else if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.current?.focus();
    }
  };

  return (
    <Modal show={show} onHide={close} centered className="template-construction" dialogClassName="lm-dialog">
      <Modal.Header closeButton className="template-construction lm-header">
        <Modal.Title className="template-construction lm-title">
          {step === "contact" ? "Sign In" : step === "name" ? "Complete Profile" : "Verify OTP"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="template-construction lm-body">
        {step === "contact" && (
          <>
            <p className="lm-sub">Enter your phone number or email to continue.</p>
            <label className="lm-label">
              Phone number or Email
              <input
                ref={inputRef}
                className="lm-input"
                type={/^\d/.test(contact) || contact === "" ? "tel" : "email"}
                inputMode={/^\d/.test(contact) || contact === "" ? "numeric" : "email"}
                placeholder="10-digit number or email address"
                value={contact}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length > 0 && /^\d/.test(val)) {
                    setContact(val.replace(/\D/g, "").slice(0, 10));
                  } else {
                    setContact(val);
                  }
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    sendOtp();
                  }
                }}
              />
            </label>
          </>
        )}
        {step === "otp" && (
          <>
            <p className="lm-sub">{message || `OTP sent to ${contact}`}</p>
            <div className="lm-otp-row" aria-label="OTP digits">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={otpRefs.current[index]}
                  className="lm-otp-box"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => updateOtpDigit(e.target.value, index)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                />
              ))}
            </div>
            <button
              className="lm-link"
              type="button"
              onClick={() => { setStep("contact"); setOtpDigits(["", "", "", ""]); setError(""); }}
            >
              ← Change number
            </button>
          </>
        )}

        {step === "name" && (
          <>
            <p className="lm-sub">Complete your profile</p>
            <label className="lm-label">
              Full Name
              <input
                className="lm-input"
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    saveName();
                  }
                }}
              />
            </label>
            <label className="lm-label mt-2">
              Email Address
              <input
                className="lm-input"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    saveName();
                  }
                }}
              />
            </label>
            {error && <p className="lm-error mt-2">{error}</p>}
            <button
              className="lm-submit mt-3"
              type="button"
              onClick={saveName}
              disabled={loading || !name.trim() || !email.trim()}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Save"}
            </button>
          </>
        )}

        {step !== "name" && (
          <>
            {error && <p className="lm-error">{error}</p>}
            <button
              className="lm-submit"
              type="button"
              onClick={step === "contact" ? sendOtp : verifyOtp}
              disabled={loading}
            >
              {loading
                ? <Spinner animation="border" size="sm" />
                : step === "contact" ? "Send OTP" : "Verify & Login"}
            </button>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default LoginModal;